import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Loader } from "components/Loader";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { StatutoryPage } from "pages/PayRoll/Shared/statutoryUi";
import { NewRequestButton } from "pages/TeamZone/Approve/approveUi";
import { getSiteOptions } from "pages/Administration/Sites/sitesApi";
import { fetchLwfOptions, fetchLwfSites } from "./lwfConfigApi";
import { LwfSitesList } from "./LwfSitesList";
import { LwfSiteConfig } from "./LwfSiteConfig";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";
import { OpenDailyScreenButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Configuration > Statutory > Labour Welfare Fund (screens
// LWF1–LWF1b): the sites list, and one site's LWF configuration with its
// versions, through the LWF API (lwfConfigApi.js). The open site is in the
// address (?item=lwf&site=2) so Back and links from other screens work.

export const LwfConfiguration = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh data per company.
  return <LwfConfigurationScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} />;
};

const LwfConfigurationScreen = ({ domain, companyId }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const { search } = useLocation();
  const history = useHistory();
  const [list, setList] = useState({ loading: true, error: "", rows: [], cards: null });
  const [options, setOptions] = useState(null);
  // Active work locations (Administration › Sites). The LWF list only has
  // sites with employees or already set up, so a new site is set up from here.
  const [workSites, setWorkSites] = useState([]);
  const [setUpRequest, setSetUpRequest] = useState(0);
  const controller = useRef(null);
  const siteId = Number(new URLSearchParams(search).get("site")) || null;

  const loadSites = useCallback(async () => {
    controller.current?.abort();
    if (!companyId) return;
    const request = new AbortController();
    controller.current = request;
    setList((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await fetchLwfSites({ domain, companyId, signal: request.signal });
      setList({ loading: false, error: "", rows: data.rows, cards: data.cards });
    } catch (err) {
      if (err.name === "AbortError") return;
      setList((current) => ({ ...current, loading: false, error: err.message || "Couldn't load the LWF sites." }));
    }
  }, [domain, companyId]);

  useEffect(() => {
    loadSites();
    if (!companyId) return () => controller.current?.abort();
    const request = new AbortController();
    fetchLwfOptions({ domain, companyId, signal: request.signal })
      .then(setOptions)
      .catch(() => {});
    getSiteOptions({ domain, signal: request.signal })
      .then((sites) => setWorkSites(Array.isArray(sites) ? sites : sites?.results || []))
      .catch(() => {});
    return () => {
      controller.current?.abort();
      request.abort();
    };
  }, [loadSites, domain, companyId]);

  // Sites LWF doesn't list yet, as "not set up" rows for the Set up picker
  // and the site page title.
  const unlisted = useMemo(
    () =>
      workSites
        .filter((site) => site?.id != null && !list.rows.some((row) => String(row.id) === String(site.id)))
        .map((site) => ({ id: Number(site.id), site: site.name || "", code: site.code || "", state: site.state || "", set_up: false })),
    [workSites, list.rows],
  );
  const knownSites = useMemo(() => [...list.rows, ...unlisted], [list.rows, unlisted]);

  const openSite = (id) => history.push({ search: `?item=lwf&site=${id}` });
  const backToList = () => {
    history.push({ search: "?item=lwf" });
    loadSites();
  };

  if (siteId) {
    return (
      <LwfSiteConfig
        key={siteId}
        domain={domain}
        companyId={companyId}
        siteId={siteId}
        siteRows={knownSites}
        options={options}
        onBack={backToList}
        onSitesChanged={loadSites}
      />
    );
  }

  return (
    <StatutoryPage
      breadcrumb={settingsCrumbs("Statutory", "Labour Welfare Fund")}
      title="Labour Welfare Fund"
      switchAction={<OpenDailyScreenButton group="statutory" item="lwf" />}
      subtitle="LWF is set up for each site. Click View to see or change a site's contributions and versions."
      aside={
        // Not set up yet: the page shows only the "Set up LWF" banner (as ESI / PF).
        list.error || (list.loading && !list.cards) || !list.rows.some((row) => row.set_up) ? null : (
          // Same header button as the Team Workspace Approve screens.
          <NewRequestButton
            hasAccess
            disabled={!list.rows.some((row) => !row.set_up) && !unlisted.length}
            onClick={() => setSetUpRequest((count) => count + 1)}
          >
            Set up site
          </NewRequestButton>
        )
      }
    >
      {list.error ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load the LWF sites."
          body={list.error}
          action={
            <SecondaryButton onClick={loadSites} sx={{ whiteSpace: "nowrap" }}>
              Retry
            </SecondaryButton>
          }
        />
      ) : list.loading && !list.cards ? (
        <Mui.Box role="status" aria-label="Loading LWF sites">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : (
        <LwfSitesList rows={list.rows} cards={list.cards} unlisted={unlisted} onOpenSite={openSite} onSetUp={openSite} addRequest={setUpRequest} />
      )}
    </StatutoryPage>
  );
};
